/**
 * This file is a part of the Promvc framework.
 * 
 * (c) ThemePlexi
 * 
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React from 'react';

import { ArrayMethods } from './helper-arraymethods.js';
import { LangMethods } from './helper-langmethods.js';

/**
 * Html template methods
 * 
 * @version 1.0.0
 */
const HtmlEntityMethods = ( function () {

	/**
	 * Declare private properties
	 * @private
	 */

	const MAX_UID = 1000000;
	const MILLISECONDS_MULTIPLIER = 1000;

	/** Tag list */
	const _map = {
		html: {
			opener: '<',
			closer: '>',
			tags: {
				a: {
					arguments: '{props}',
					isolated: false
				},
				code: {
					arguments: '{props}',
					isolated: false
				},
				div: {
					arguments: '{props}',
					isolated: false
				},
				span: {
					arguments: '{props}',
					isolated: false
				}
			}
		}
	};

	/** Tag map */
	const _tagMap = {
		class: 'className'
	};


	/**
	 * Public html methods api.
	 * 
	 * @since 1.0.0
	 */

	const Util = {

		/**
		 * Convert HTML attributes to object properties.
		 * 
		 * @param  {string} source     The string representation of HTML attributes.
		 * @param  {string} expression The expression.
		 * @return {object}
		 */
		_arguments: function ( source, expression )
		{
			let props = {};

			if ( typeof source === 'string' && expression === '{props}' ) {
				let arr = source.trim().split( ' ' );
				for ( let prop of arr ) {
					let x = prop.replace( /['"]/g, '' ).split( '=' );
					if ( x.length > 1 ) {
						x[ 0 ] = _tagMap[ x[ 0 ] ] !== undefined ? _tagMap[ x[ 0 ] ] : x[ 0 ];
						props[ x[ 0 ] ] = x[ 1 ];
					}
				}
			}

			return props;
		},

		/**
		 * Parse the HTML string into an array of entities.
		 * 
		 * @param  {string} source The original HTML string to parse into an array.
		 * @return {array}         The parsed array of entities.
		 */
		array: function ( source )
		{
			let parts = [],
				tags = [],
				all = [];

			// Make sure sorce is the correct type.
			if ( typeof source !== 'string' ) {
				return false;
			}

			while ( source ) {
				const match = this.match( source );

				let opener = null,
					closer = null;

				if ( match !== null ) {
					opener = source.indexOf( match[ 'type' ][ 'opener' ] );
					closer = source.indexOf( match[ 'type' ][ 'closer' ] ) + match[ 'type' ][ 'closer' ][ 'length' ];
				}

				if ( opener !== null && opener !== -1 ) {
					parts.push( source.substring( 0, opener ) );
					tags.push( source.substring( opener, closer ) );
					source = source.substring( closer );
				} else {
					parts.push( source );
					source = '';
				}
			}

			for ( let i = 0, len = parts.length; i < len; i++ ) {
				all.push( parts[ i ] );
				if ( tags[ i ] !== undefined ) {
					all.push( tags[ i ] );
				}
			}

			return {
				text: ArrayMethods.clean( parts ),
				tags: ArrayMethods.clean( tags ),
				all: ArrayMethods.clean( all )
			};
		},

		/**
		 * Create react elements.
		 * 
		 * @param {*array} nodes The html node list.
		 * @returns array
		 */
		createNodes: function ( nodes )
		{
			let nodeArr = [];

			if ( nodes && nodes.length > 0 )
			{
				for ( let i = 0; i < nodes.length; i++ )
				{
					if ( nodes[ i ].nodeType != 3 ) {
						let obj = React.createElement(
							nodes[ i ].localName,
							{
								key: i,
								className: nodes[ i ].className
							},
							( nodes[ i ].childNodes.length > 0 ? this.createNodes( nodes[ i ].childNodes ) : null )
						);

						nodeArr.push( obj );
					} else {
						nodeArr.push( nodes[ i ].nodeValue );
					}
				}
			}

			return nodeArr;
		},
	
		/**
		 * Generate React components from HTML tree.
		 * 
		 * @param  {array} tree The HTML tree structure to parse.
		 * @return {array}      The React components
		 */
		generateComponent: function ( tree )
		{

			let content = [];

			if ( typeof tree === 'string' ) {
				return tree;
			}

			if ( tree[ 'children' ] !== undefined && tree[ 'children' ].length > 0 ) {

				for ( let child of tree[ 'children' ] ) {
					content.push( this.generateComponent( child ) );
				}
			}

			if ( tree[ 'parent' ] !== undefined ) {
				return React.createElement( tree[ 'tag' ], { ...tree[ 'arguments' ] }, content );
			}

			return content;
		},

		/**
		 * Get reference selector from element attribute.
		 * 
		 * @param {Object} element The html dom element to search.
		 * @return {String|null}
		 */
		getSelectorFromElement( element )
		{
			let selector = element.getAttribute( 'data-target' );
	
			if ( ! selector || selector === '#' ) {
				const hrefAttr = element.getAttribute( 'href' );
				selector = hrefAttr && hrefAttr !== '#' ? hrefAttr.trim() : '';
			}
	
			try {
				return document.querySelector( selector ) ? selector : null;
			} catch ( _ ) {
				return null;
			}
		},

		/**
		 * Get the css transition duration for a dom element.
		 * 
		 * @param  {Object} element The html dom element.
		 * @return {Int}
		 */
		getTransitionDurationFromElement( element )
		{
			if ( ! element ) {
				return 0;
			}
	
			// Get transition-duration of the element
			let computedStyles = window.getComputedStyle( element ),
				transitionDuration = computedStyles.getPropertyValue( 'transition-duration' ),
				transitionDelay = computedStyles.getPropertyValue( 'transition-delay' );
	
			const floatTransitionDuration = parseFloat( transitionDuration );
			const floatTransitionDelay = parseFloat( transitionDelay );
	
			// Return 0 if element or transition duration is not found
			if ( ! floatTransitionDuration && ! floatTransitionDelay ) {
				return 0;
			}
	
			// If multiple durations are defined, take the first
			transitionDuration = transitionDuration.split( ',' )[ 0 ];
			transitionDelay = transitionDelay.split( ',' )[ 0 ];
	
			return ( parseFloat( transitionDuration ) + parseFloat( transitionDelay ) ) * MILLISECONDS_MULTIPLIER;
		},
		
		/**
		 * Generate a unique dom element id.
		 * 
		 * @param  {String} prefix The id prefix
		 * @return {String}
		 */
		getUID( prefix )
		{
			do {
				prefix += ~~( Math.random() * MAX_UID ); // "~~" acts like a faster Math.floor() here
			} while ( document.getElementById( prefix ) );
	
			return prefix;
		},

		/**
		 * Match HTML tag.
		 * 
		 * @param  {string} source The string to match against.
		 * @return {array}         The resultant tag match.
		 */
		match: function ( source )
		{
			let delimiter = null,
				type = null;

			// Make sure sorce is the correct type.
			if ( typeof source !== 'string' ) {
				return false;
			}

			for ( let _delimiter in _map ) {
				if ( ! Object.prototype.hasOwnProperty.call( _map, _delimiter ) ) {
					continue;
				}

				if ( ! delimiter || source.indexOf( type[ 'opener' ] ) === -1 ) {
					delimiter = _delimiter;
					type = _map[ _delimiter ];
				}

				let index = source.indexOf( _map[ _delimiter ][ 'opener' ] );

				if ( index > -1 ) {

					// Make sure we are assigning the left most tag match
					if ( source.indexOf( type[ 'opener' ] ) > index ) {
						delimiter = _delimiter;
						type = _map[ _delimiter ];
					}
				}
			}

			if ( type === null ) {
				return null;
			}

			return {
				delimiter,
				type
			};
		},

		/**
		 * Parse an html encoded string into a React component.
		 * 
		 * @param  {string} template The HTML encoded string.
		 * @return {object}          The React component object.
		 */
		parse: function ( template )
		{
			if ( template.indexOf( '<' ) === -1 ) {
				return template;
			}

			const arr = this.array( template );
			const tree = this.tree( arr[ 'all' ] );
	
			// Generate content
			const content = this.generateComponent( tree );

			// Debug
			// console.log( content );
	
			return content;
		},

		/**
		 * Parse HTML tag.
		 * 
		 * @param {string} source The HTML string.
		 * @return {array|boolean} The converted tag if its an HTML tag or false otherwise.
		 */
		tag: function ( source )
		{
			const match = this.match( source );
			
			let tag, args, closer;

			if ( match === null ) {
				return false;
			}

			const delimiter = match[ 'delimiter' ];
			const type = match[ 'type' ];

			const start = type[ 'opener' ].length;
			const end = source.indexOf( type[ 'closer' ] );

			let extract = source.substring( start, end );

			if ( type[ 'tags' ] !== undefined ) {
				const tags = Object.keys( type[ 'tags' ] ).join( '|' );
				const regex = new RegExp( `^(/){0,1}(${tags})s*(.*)$` );

				const matches = extract.match( regex );
				if ( ! matches ) {
					return false;
				}

				closer = !! matches[ 1 ];
				extract = matches[ 3 ];
				tag = matches[ 2 ];
			}

			if ( tag && closer )
			{
				return {
					arguments: false,
					closer: true,
					delimiter,
					isolated: type[ 'tags' ][ tag ][ 'isolated' ],
					source: false,
					tag
				};
			}

			if ( type[ 'arguments' ] !== undefined ) {
				args = this._arguments( extract, type[ 'arguments' ] );
			} else if ( tag && type[ 'tags' ][ tag ][ 'arguments' ] !== undefined ) {
				args = this._arguments( extract, type[ 'tags' ][ tag ][ 'arguments' ] );
			}

			return {
				arguments: args,
				closer: false,
				delimiter,
				isolated: ( type[ 'tags' ] !== undefined && ! LangMethods.isEmpty( type[ 'tags' ] ) ) ? type[ 'tags' ][ tag ][ 'isolated' ] : false,
				source: extract.trim(),
				tag,
			};
		},

		/**
		 * Convert an HTML array into its correspounding tree representation.
		 * 
		 * @param  {array} arr The input HTML tree structure.
		 * @return {array}     The input HTML tree structure with tags converted.
		 */
		tree: function ( arr )
		{
			let root = { children: [] },
				current = root;

			for ( let i = 0, len = arr.length; i < len; i++ ) {
				const result = this.tag( arr[ i ] );

				if ( result ) {
					const tag = result[ 'tag' ] !== undefined ? result[ 'tag' ] : '';
					const args = result[ 'arguments' ] !== undefined ? result[ 'arguments' ] : '';

					if ( tag ) {

						if ( ! result[ 'closer' ] ) {
							const last = ArrayMethods.last( current[ 'children' ] );

							if ( result[ 'isolated' ] !== undefined && result[ 'isolated' ] && typeof last === 'string' ) {
								current[ 'children' ].pop();
							}

							current[ 'children' ].push({
								arguments: args,
								children: [],
								delimiter: result[ 'delimiter' ],
								index: i,
								number: current.children.length,
								parent: current,
								raw: result[ 'source' ],
								tag
							});

							current = current[ 'children' ][ current[ 'children' ].length - 1 ];

						} else if ( current[ 'tag' ] !== undefined && result[ 'tag' ] === current[ 'tag' ] ) {
							const start = current[ 'index' ] + 1;
							const end = i;
							current[ 'source' ] = arr.slice( start, end ).join( '' );
							current = current[ 'parent' ];
						}
						
					} else {
						current[ 'children' ].push({
							arguments: args,
							children: [],
							delimiter: result[ 'delimiter' ],
							index: i,
							number: current[ 'children' ].length,
							parent: current,
							raw: result[ 'source' ],
							tag
						});
					}

				} else {
					current[ 'children' ].push( arr[ i ] );
				}
			}

			// Debug
			// console.log( root );

			return root;
		}
	
	};

	return Util;

}());

/**
 * Module exports
 */
export { HtmlEntityMethods };